<template>
  <LcCarousel
    :id="`${PLAY_SLIP_EDITOR_ID}-carousel`"
    ref="carousel"
    class="lottery-base-play-slip -mt-100"
    :default-index="getPlayIndex(currentPlayId)"
    :disable-swipe="isDisabled"
    key-prop="id"
    loop
    :slides="plays"
    @changed="updateVisiblePlayId"
  >
    <template #slide="{ attributes, index, isVisible, slide: play }">
      <div
        v-bind="attributes"
        :id="`${PLAY_SLIP_EDITOR_ID}-tabpanel-${index}`"
        :aria-label="
          $t('common.carousel.slide.arialabel', {
            slide: index + 1,
            totalSlides: plays.length,
          })
        "
        class="slide pt-100"
        data-containerid="playslip_editor"
        role="tabpanel"
      >
        <slot v-bind="{ isVisible, play, updatePlays }" :is-disabled="visiblePlayId !== play.id" />
      </div>
    </template>
  </LcCarousel>

  <nav class="flex justify-center items-center mt-100 mb-300 gap-300">
    <LcButton
      :aria-controls="`${PLAY_SLIP_EDITOR_ID}-carousel`"
      :aria-label="$t('common.carousel.button.prev')"
      :disabled="isDisabled"
      icon="chevron-left"
      size="small"
      variant="filled"
      @click="() => carouselRef?.prev()"
    />
    <LotteryClearNumbersButton :play-id="visiblePlayId" />
    <LcButton
      :aria-controls="`${PLAY_SLIP_EDITOR_ID}-carousel`"
      :aria-label="$t('common.carousel.button.next')"
      :disabled="isDisabled"
      icon="chevron-right"
      size="small"
      variant="filled"
      @click="() => carouselRef?.next()"
    />
  </nav>

  <slot name="bottom">
    <LotteryBaseQuickPickButton />
  </slot>
</template>

<script lang="ts" setup>
import { COOKIE_SWIPE_EXPLAINER } from '~/constants/cookies'

import { PLAY_SLIP_EDITOR_ID } from '../constants/playSlip'
import { useLotteryStore } from '../stores/lottery'

type RouteQueryParams = {
  playId?: string
}

const cookie = useCookie(COOKIE_SWIPE_EXPLAINER, { default: () => false })
const { setPlays } = useLotteryStore()
const {
  addEmptyPlay,
  firstIncompletePlayId,
  getPlayIndex,
  hasEmptyPlays,
  lastEditedPlayId,
  lotteryId,
  maxAllowedPlays,
  plays,
  removePlayByIndex,
  visiblePlayId,
} = usePlaySlip()
const query = useRoute()?.query as RouteQueryParams

const carouselRef = useTemplateRef('carousel')
const isPlaySlipInitialized = ref(false)

const currentPlayId = computed(() => {
  const playId = plays.value.find((play) => play.id === query?.playId)?.id
  return playId || firstIncompletePlayId.value || lastEditedPlayId.value
})
const isDisabled = computed(() => plays.value.length <= 1)

const goToPlay = (playId: string, animated = true) => {
  carouselRef.value?.moveToIndex(getPlayIndex(playId), animated)
}

const initPlaySlip = () => {
  goToPlay(currentPlayId.value, false)
  updatePlaysWithId(currentPlayId.value)

  visiblePlayId.value = currentPlayId.value
  isPlaySlipInitialized.value = true
}

const showSwipeHint = async () => {
  if (maxAllowedPlays.value === 1 || cookie.value) return

  if (plays.value.find((play) => play.id === visiblePlayId.value)?.isComplete) {
    await carouselRef.value?.showSwipeHint()
    cookie.value = true
  }
}

const updatePlays = async (isPlayComplete: boolean, playId: string) => {
  if (playId !== visiblePlayId.value) return
  const currentPlayIndex = getPlayIndex(playId)
  const dirtyAndNotCompletePlayIndex = plays.value.findIndex(
    (play) => play.isDirty && !play.isComplete && play.id !== playId,
  )
  const notCompletePlayIndex = plays.value.findIndex(
    (play) => !play.isComplete && play.id !== playId,
  )
  const notDirtyPlayIndex = plays.value.findIndex((play) => !play.isDirty && play.id !== playId)

  if (
    isPlayComplete &&
    notCompletePlayIndex === -1 &&
    dirtyAndNotCompletePlayIndex === -1 &&
    plays.value.length < maxAllowedPlays.value
  ) {
    if (!hasEmptyPlays.value) addEmptyPlay()
    if (isPlaySlipInitialized.value) {
      goToPlay(playId, false)
      await showSwipeHint()
    }
  }

  if (
    (!isPlayComplete && notDirtyPlayIndex !== -1) ||
    (isPlayComplete && dirtyAndNotCompletePlayIndex >= 0 && notDirtyPlayIndex >= 0)
  ) {
    if (currentPlayIndex > notDirtyPlayIndex) {
      visiblePlayId.value = plays.value[currentPlayIndex - 1].id
    }
    removePlayByIndex(notDirtyPlayIndex)
  }
}

const updatePlaysWithId = (id: string) => {
  const isPlayComplete = plays.value[getPlayIndex(id)]?.isComplete
  updatePlays(isPlayComplete, id)
}

const updateVisiblePlayId = (index: number) => {
  const playId = plays.value.find((_, idx) => idx === index)?.id
  if (!playId) return

  visiblePlayId.value = playId
}

onBeforeMount(() => setPlays(lotteryId, plays.value))
onMounted(initPlaySlip)

watch(visiblePlayId, (newValue, oldValue) => {
  if (newValue === oldValue) return
  goToPlay(newValue)
  updatePlaysWithId(newValue)
})

// When the stored plays change underneath the slip -- e.g. invalid plays are
// discarded asynchronously after deserialization -- visiblePlayId can point at
// a play that no longer exists, which disables and greys out the slide. Resync
// it to a valid play so the slip stays interactive. (SF-6119)
watch(
  () => plays.value.map((play) => play.id).join(','),
  () => {
    if (plays.value.some((play) => play.id === visiblePlayId.value)) return
    visiblePlayId.value = currentPlayId.value
  },
)
</script>

<style lang="scss" scoped>
.lottery-base-play-slip {
  container-type: inline-size;

  .slide {
    min-width: 100cqw;
  }
}
</style>
